import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Asterisk, RotateCcw, History, X, Loader2 } from "lucide-react";
import { ago, when, CLAUDE_TONE } from "@/components/claude/ClaudeBadge";

const RP_KIND = { auto: "Saved automatically", named: "Named by Claude", pre_undo: "Before an undo" };

export function useClaudeChanges(bidId) {
  const [data, setData] = useState(null);
  const reload = useCallback(() => api.get(`/claude/bids/${bidId}/changes`).then((r) => setData(r.data)).catch(() => {}), [bidId]);
  useEffect(() => {
    reload();
    const t = setInterval(() => { if (!document.hidden) reload(); }, 60000);
    return () => clearInterval(t);
  }, [reload]);
  return { data, reload };
}

function useRestore(bidId, reload, onRestored) {
  const [busy, setBusy] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const run = async (rpId, force = false) => {
    setBusy(rpId || "latest");
    try {
      const r = await api.post(`/claude/bids/${bidId}/restore`, { rp_id: rpId || null, confirm: force });
      if (r.data.needs_confirm) { setConfirm({ rp: r.data.restore_point, lost: r.data.would_lose }); return; }
      setConfirm(null);
      toast.success(`Bid put back to "${r.data.restored_to.label}". You can reverse this from Claude's changes.`);
      onRestored?.();
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not put the bid back"); }
    finally { setBusy(null); }
  };
  return { busy, confirm, setConfirm, run };
}

function RestoreConfirmDialog({ r }) {
  const c = r.confirm;
  return (
    <Dialog open={!!c} onOpenChange={(o) => { if (!o) r.setConfirm(null); }}>
      <DialogContent data-testid="claude-restore-confirm">
        <DialogHeader>
          <DialogTitle>These edits will be lost</DialogTitle>
          <DialogDescription>
            People edited this bid after the restore point "{c?.rp?.label}". Putting the bid back removes their edits too.
            GridForge saves a restore point first, so this can be reversed.
          </DialogDescription>
        </DialogHeader>
        <ul className="divide-y divide-slate-100 rounded-md border border-slate-200 text-sm max-h-60 overflow-auto">
          {(c?.lost || []).map((l, i) => (
            <li key={i} data-testid={`claude-lost-${i}`} className="px-3 py-2 flex justify-between gap-3">
              <span className="text-slate-700"><b>{l.user_name}</b> · {l.label}{l.count > 1 ? ` (${l.count} times)` : ""}</span>
              <span className="text-xs text-slate-400 shrink-0">{when(l.last_at)}</span>
            </li>
          ))}
        </ul>
        <DialogFooter>
          <Button variant="ghost" data-testid="claude-restore-cancel" onClick={() => r.setConfirm(null)}>Keep the bid as is</Button>
          <Button data-testid="claude-restore-confirm-btn" disabled={!!r.busy} onClick={() => r.run(c.rp.rp_id, true)}
            className="bg-rose-600 hover:bg-rose-700 text-white">
            {r.busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Put it back anyway
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RestorePoints({ data, r }) {
  if (!data.restore_points.length) return <div className="text-xs text-slate-400">No restore points yet.</div>;
  return (
    <div className="divide-y divide-slate-100 rounded-md border border-slate-200" data-testid="claude-restore-points">
      {data.restore_points.map((p) => (
        <div key={p.rp_id} data-testid={`claude-rp-${p.rp_id}`} className="flex items-center gap-3 px-3 py-2">
          <div className="min-w-0 flex-1">
            <div className="text-sm text-slate-800 truncate">{p.label}</div>
            <div className="text-[11px] text-slate-400">{RP_KIND[p.kind] || p.kind} · {when(p.at)} · {p.user_name}</div>
          </div>
          {data.can_restore && (
            <Button size="sm" variant="outline" data-testid={`claude-rp-restore-${p.rp_id}`} disabled={!!r.busy}
              onClick={() => r.run(p.rp_id)} className="h-8 text-[#003E87] border-[#003E87]/30">
              {r.busy === p.rp_id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5 mr-1" />}Put back
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}

function ChangeList({ changes }) {
  if (!changes.length) return <div className="text-xs text-slate-400">No changes yet.</div>;
  return (
    <div className="divide-y divide-slate-100 rounded-md border border-slate-200 max-h-64 overflow-auto" data-testid="claude-change-list">
      {changes.map((c) => (
        <div key={c.change_id} data-testid={`claude-change-${c.change_id}`} className="flex items-start gap-3 px-3 py-2">
          <Asterisk className="w-3.5 h-3.5 mt-0.5 text-[#D97757] shrink-0" />
          <div className="min-w-0 flex-1">
            <div className={`text-sm ${c.undone_at ? "text-slate-400 line-through" : "text-slate-800"}`}>{c.summary}</div>
            <div className="text-[11px] text-slate-400">{c.user_name} · {when(c.at)}{c.undone_at ? " · undone" : ""}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ChangesDialog({ open, onOpenChange, data, r }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="claude-changes-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Asterisk className="w-4 h-4 text-[#D97757]" /> Claude's changes</DialogTitle>
          <DialogDescription>Restore points are kept for 30 days. Putting the bid back saves a new restore point first, so it can be reversed.</DialogDescription>
        </DialogHeader>
        {data.locked && <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">This bid is sent or closed, so it can't be put back. Open a revision instead.</div>}
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Restore points</div>
        <RestorePoints data={data} r={r} />
        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 pt-1">Changes</div>
        <ChangeList changes={data.changes} />
      </DialogContent>
    </Dialog>
  );
}

export function ClaudeBanner({ bidId, state, onRestored }) {
  const { data, reload } = state;
  const [open, setOpen] = useState(false);
  const r = useRestore(bidId, reload, onRestored);
  if (!data || !data.new_count) return <><ChangesHolder open={open} setOpen={setOpen} data={data} r={r} /><RestoreConfirmDialog r={r} /></>;
  const dismiss = () => api.post(`/claude/bids/${bidId}/dismiss`).catch(() => {}).finally(reload);
  return (
    <>
      <div data-testid="claude-banner" className="shrink-0 bg-[#FBF1EC] border-b border-[#D97757]/25 px-5 py-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#7A3A1F]">
        <Asterisk className="w-4 h-4 text-[#D97757] shrink-0" />
        <span className="flex-1 min-w-[160px]" data-testid="claude-banner-text">
          <b>Claude made {data.new_count} change{data.new_count === 1 ? "" : "s"}</b> · {ago(data.last_at)}
        </span>
        <Button size="sm" variant="ghost" data-testid="claude-banner-view" onClick={() => setOpen(true)} className="h-7 text-[#7A3A1F] hover:bg-[#D97757]/10">
          <History className="w-3.5 h-3.5 mr-1" /> View changes
        </Button>
        {data.can_restore && data.undo_rp && (
          <Button size="sm" variant="outline" data-testid="claude-banner-undo" disabled={!!r.busy} onClick={() => r.run(data.undo_rp.rp_id)}
            className="h-7 border-[#D97757]/40 text-[#7A3A1F] bg-white hover:bg-[#D97757]/10">
            {r.busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5 mr-1" />} Undo
          </Button>
        )}
        <button data-testid="claude-banner-dismiss" onClick={dismiss} title="Dismiss once you've reviewed the changes"
          className="p-1 rounded text-[#B4532F] hover:bg-[#D97757]/10"><X className="w-3.5 h-3.5" /></button>
      </div>
      <ChangesHolder open={open} setOpen={setOpen} data={data} r={r} />
      <RestoreConfirmDialog r={r} />
    </>
  );
}

function ChangesHolder({ open, setOpen, data, r }) {
  if (!data) return null;
  return <ChangesDialog open={open} onOpenChange={setOpen} data={data} r={r} />;
}

export function ClaudeChip({ bidId, state, onRestored }) {
  const { data, reload } = state;
  const [open, setOpen] = useState(false);
  const r = useRestore(bidId, reload, onRestored);
  if (!data || !data.changes.length) return null;
  const fresh = data.new_count || 0;
  const openAndClear = () => {
    setOpen(true);
    if (fresh) api.post(`/claude/bids/${bidId}/dismiss`).catch(() => {}).finally(reload);
  };
  return (
    <>
      <button data-testid="claude-history-chip" onClick={openAndClear} title={fresh ? `${fresh} new change${fresh === 1 ? "" : "s"} via Claude · ${ago(data.last_at)}` : "Changes made through Claude"}
        className={`relative inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded hover:opacity-80 ${CLAUDE_TONE}`}>
        <Asterisk className="w-3 h-3" /> Claude history
        {fresh > 0 && <span data-testid="claude-history-new" className="ml-0.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-[#D97757] text-white text-[9px] font-bold">{fresh}</span>}
      </button>
      <ChangesDialog open={open} onOpenChange={setOpen} data={data} r={r} />
      <RestoreConfirmDialog r={r} />
    </>
  );
}
